import type { EnhanceOptions } from "@/app/dto/enhance-signature.dto.ts";
import { usePendingSignaturesStore } from "@/app/stores/pending-signatures.store.ts";
import { resize } from "@/app/utils/resize.ts";
import {
  PdfAnnotationSubtype,
  type PdfStampAnnoObject,
  type Rect,
  restoreRect,
  type Size,
  transformRect,
  transformSize,
  uuidV4,
} from "@embedpdf/models";
import { type AnnotationScope } from "@embedpdf/plugin-annotation/vue";
import type { useActiveDocument } from "@embedpdf/plugin-document-manager/vue";
import { type ScrollScope } from "@embedpdf/plugin-scroll";
import { type Ref } from "vue";
import { AbstractSignObject, type SignCoordinates } from "./abstract.sign-object.domain.ts";
import { getPageRotation } from "./page-rotation.util.ts";

export class PendingSignature extends AbstractSignObject {
  static readonly ANNOTATION_TYPE = PdfAnnotationSubtype.STAMP;

  private readonly id: string;
  public readonly name = "signature";
  private readonly pendingSignaturesStore: ReturnType<typeof usePendingSignaturesStore>;

  constructor(
    public readonly signObjectId: number,
    public readonly enhanceOptions: EnhanceOptions,
    imageSize: Size,
    annotationApi: () => Ref<AnnotationScope | null>,
    scrollProvide: () => Ref<ScrollScope | null>,
    activeDocument: () => ReturnType<typeof useActiveDocument>,
    initialCoordinates?: SignCoordinates
  ) {
    const isEnhanced = Object.values(enhanceOptions).some(value => value);
    const imageRatio = imageSize.height / imageSize.width;
    const resizedCoordinates =
      initialCoordinates && resizeCoordinates(initialCoordinates, imageRatio, isEnhanced, activeDocument());
    super(annotationApi, scrollProvide, activeDocument, resizedCoordinates, imageSize);
    this.id = uuidV4();
    this.pendingSignaturesStore = usePendingSignaturesStore();
    this.pendingSignaturesStore.add(this);
  }

  get idStr(): string {
    return this.id;
  }

  async delete() {
    await Promise.all([super.delete(), this.pendingSignaturesStore.remove(this)]);
  }

  public buildAnnotation(): PdfStampAnnoObject {
    return {
      ...this.getBaseAnnotation(),
      type: PendingSignature.ANNOTATION_TYPE,
    };
  }
}

// Fits imageRatio within the preposition's box, anchored top-left. The box is
// stored raw, but "fitting" is only meaningful in display (rotated) space, so
// convert there and back.
function resizeCoordinates(
  { width, height, ...initialCoordinates }: SignCoordinates,
  imageRatio: number,
  isEnhanced: boolean,
  activeDocument: ReturnType<typeof useActiveDocument>
): SignCoordinates {
  const page = activeDocument.activeDocument.value?.document?.pages.find(
    candidate => candidate.index === initialCoordinates.pageIndex
  );
  const rawSize: Size = page?.size ?? { width, height };
  const rotation = getPageRotation(initialCoordinates.pageIndex, activeDocument.activeDocument.value);

  const rawRect: Rect = {
    origin: { x: initialCoordinates.upperLeftPoint.x, y: rawSize.height - initialCoordinates.upperLeftPoint.y },
    size: { width, height },
  };
  const displaySize = transformSize(rawSize, rotation, 1);
  const displayRect = transformRect(rawSize, rawRect, rotation, 1);
  const fittedSize = resize({ width: displayRect.size.width, height: displayRect.size.height }, imageRatio);
  const fittedRawRect = restoreRect(displaySize, { origin: displayRect.origin, size: fittedSize }, rotation, 1);

  const resizedWithImageRatio: SignCoordinates = {
    pageIndex: initialCoordinates.pageIndex,
    upperLeftPoint: { x: fittedRawRect.origin.x, y: rawSize.height - fittedRawRect.origin.y },
    width: fittedRawRect.size.width,
    height: fittedRawRect.size.height,
  };
  if (!isEnhanced) {
    return resizedWithImageRatio;
  }

  const WIDER = 0.3;
  const TALLER = 0.45;
  const wider = resizedWithImageRatio.width * (1 + WIDER);
  const taller = resizedWithImageRatio.height * (1 + TALLER);
  return {
    upperLeftPoint: {
      x: resizedWithImageRatio.upperLeftPoint.x - resizedWithImageRatio.width * WIDER,
      y: resizedWithImageRatio.upperLeftPoint.y + resizedWithImageRatio.height * TALLER,
    },
    height: taller,
    width: wider,
    pageIndex: resizedWithImageRatio.pageIndex,
  };
}
